import React from "react";

class SearchableSelect extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
      searchTerm: "",
      previousValue: null,
      selectedLabel: "",
    };
    this.dropdownRef = React.createRef();
    this.searchTimeout = null;
  }

  getOptionValue = (option) => {
    if (!option) return null;
    return option.value !== undefined ? option.value : option.id;
  };

  getOptionLabel = (option) => {
    if (!option) return "";
    if (option.label !== undefined) return option.label;
    if (option.name !== undefined) return option.name;
    return "";
  };

  componentDidUpdate(prevProps) {
    const prevValue = prevProps.value;
    const nextValue = this.props.value;
    const prevOptions = prevProps.options;
    const nextOptions = this.props.options;

    if (prevValue !== nextValue || prevOptions !== nextOptions) {
      const selectedOption = (nextOptions || []).find(option => String(this.getOptionValue(option)) === String(nextValue));
      if (selectedOption) {
        const nextLabel = this.getOptionLabel(selectedOption);
        if (nextLabel && nextLabel !== this.state.selectedLabel) {
          this.setState({ selectedLabel: nextLabel });
        }
      }
    }
  }

  componentDidMount() {
    document.addEventListener("mousedown", this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
  }

  handleClickOutside = (event) => {
    if (this.dropdownRef.current && !this.dropdownRef.current.contains(event.target)) {
      this.restoreValue();
    }
  };

  handleInputChange = (e) => {
    const nextTerm = e.target.value;
    this.setState({ searchTerm: nextTerm, isOpen: true });

    const { onSearch, minSearchLength = 2, debounceMs = 300 } = this.props;
    if (!onSearch) return;

    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      const trimmed = String(nextTerm || '').trim();
      if (trimmed.length < minSearchLength) return;
      onSearch(trimmed);
    }, debounceMs);
  };

  handleOptionClick = (option) => {
    const { name, onChange } = this.props;
    const optionValue = this.getOptionValue(option);
    const optionLabel = this.getOptionLabel(option);
    onChange({ currentTarget: { name, value: optionValue } });
    this.setState({ searchTerm: optionLabel, isOpen: false, previousValue: null, selectedLabel: optionLabel });
  };

  toggleDropdown = () => {
    const { isOpen, searchTerm, previousValue } = this.state;
    const { value } = this.props;
    const options = Array.isArray(this.props.options) ? this.props.options : [];
    
    if (!isOpen) {
      const selectedOption = options.find(option => String(this.getOptionValue(option)) === String(value));
      this.setState({
        isOpen: true,
        searchTerm: "",
        previousValue: selectedOption ? this.getOptionLabel(selectedOption) : (previousValue || searchTerm)
      });
    } else {
      this.restoreValue();
    }
  };

  restoreValue = () => {
    const { previousValue } = this.state;
    this.setState({ isOpen: false, searchTerm: previousValue || "", previousValue: null });
  };

  render() {
    const { name, label, error, disabled, value, showLabel, style } = this.props; // Added style prop
    const options = Array.isArray(this.props.options) ? this.props.options : [];
    const { isOpen, searchTerm, selectedLabel } = this.state;

    const selectedOption = options.find(option => String(this.getOptionValue(option)) === String(value));
    const selectedOptionLabel = selectedOption ? this.getOptionLabel(selectedOption) : "";
    const displayValue = isOpen ? searchTerm : (selectedOptionLabel || selectedLabel || (value !== undefined && value !== null ? String(value) : ""));
    const filteredOptions = options.filter(option =>
      this.getOptionLabel(option).toLowerCase().includes(searchTerm.toLowerCase())
    );

    const compact = showLabel === false;

    if (!compact) {
      return (
        <div className="form-group">
          <div className="row">
            <label
              htmlFor={name}
              className="col-sm-3 col-form-label col-form-label-sm d-flex justify-content-sm-end align-items-sm-end pe-3 text-muted"
            >
              {label}:
            </label>
            <div className="col-sm-8">
              <div className="position-relative" ref={this.dropdownRef} style={style}>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder={label ? `Select ${label}` : 'Select...'}
                    value={displayValue}
                    onChange={this.handleInputChange}
                    onClick={this.toggleDropdown}
                    disabled={disabled}
                    style={{ borderRadius: '0.2rem', paddingRight: '30px' }}
                  />
                  <button
                    className="form-control-sm btn btn-sm"
                    type="button"
                    onClick={this.toggleDropdown}
                    disabled={disabled}
                    style={{
                      position: 'absolute',
                      right: '0px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '30px',
                      border: 'none',
                      borderRadius: '0.2rem',
                      boxShadow: 'none',
                      padding: '0',
                    }}
                  >
                    <i className={`fa fa-angle-${isOpen ? 'up' : 'down'}`}></i>
                  </button>
                </div>
                {isOpen && (
                  <ul className="list-group position-absolute w-100 mt-1" style={{ zIndex: 1000, fontSize: '0.875rem', top: '100%', left: 0 }}>
                    {filteredOptions.map((option) => (
                      <li
                        key={this.getOptionValue(option)}
                        className="list-group-item list-group-item-action py-1"
                        onClick={() => this.handleOptionClick(option)}
                      >
                        {this.getOptionLabel(option)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {error && <div className="alert alert-danger small p-0 mt-1">{error}</div>}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="position-relative" ref={this.dropdownRef} style={{ width: '200px', marginRight: '10px', ...style }}>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Select..."
            value={displayValue}
            onChange={this.handleInputChange}
            onClick={this.toggleDropdown}
            disabled={disabled}
            style={{ borderRadius: '0.2rem', paddingRight: '30px' }} // Add padding for button
          />
          <button 
            className="form-control-sm btn btn-sm"
            type="button"
            onClick={this.toggleDropdown}
            disabled={disabled}
            style={{
              position: 'absolute',
              right: '0px', // Position the button inside the input
              top: '50%',
              transform: 'translateY(-50%)', // Center the button vertically
              width: '30px',
              border: 'none',
              borderRadius: '0.2rem',
              boxShadow: 'none',
              padding: '0', // Remove padding
            }}
          >
            <i className={`fa fa-angle-${isOpen ? 'up' : 'down'}`}></i>
          </button>
        </div>
        {isOpen && (
          <ul className="list-group position-absolute w-100 mt-1" style={{ zIndex: 1000, fontSize: '0.875rem', top: '100%', left: 0 }}>
            {filteredOptions.map((option) => (
              <li
                key={this.getOptionValue(option)}
                className="list-group-item list-group-item-action py-1"
                onClick={() => this.handleOptionClick(option)}
              >
                {this.getOptionLabel(option)}
              </li>
            ))}
          </ul>
        )}
        {error && <div className="alert alert-danger small p-0 mt-1">{error}</div>}
      </div>
    );
  }
}

export default SearchableSelect;